/*
 * Copyright © 2015-2017 Serenova, LLC. All rights reserved.
 */

import { fromJS, List } from 'immutable';
import { generateUUID } from 'serenova-js-utils/uuid';
import { findEntityIndex, getSelectedEntityWithIndex } from './selectors';
import { entitiesMetaData } from './metaData';

const jwt = require('jsonwebtoken');

// Initial Sub State
const defaultEntity = {
  data: [],
  selectedEntityId: '',
  sidePanelWidth: 550,
  confirmationDialogType: undefined,
};

// Permissions shared by businessHour and businessHourV2 entities
const businessHourPermisssions = {
  readPermission: ['VIEW_ALL_BUSINESS_HOURS'],
  updatePermission: ['MANAGE_ALL_BUSINESS_HOURS'],
  createPermission: ['MANAGE_ALL_BUSINESS_HOURS'],
  disablePermission: ['MANAGE_ALL_BUSINESS_HOURS'],
  sharePermission: ['MANAGE_ALL_BUSINESS_HOURS'],
};

const initialState = fromJS({
  currentEntity: 'none',
  none: {},
  interactionMonitoring: {
    readPermission: ['VIEW_ALL_MONITORED_CALLS'],
  },
  agentStateMonitoring: {
    ...defaultEntity,
    readPermission: ['MANAGE_ALL_USER_STATE', 'MANAGE_ALL_USERS_DIRECTION'],
  },
  identityProviders: {
    ...defaultEntity,
    readPermission: ['IDENTITY_PROVIDERS_CREATE'],
    updatePermission: ['IDENTITY_PROVIDERS_UPDATE'],
    createPermission: ['IDENTITY_PROVIDERS_CREATE'],
    deletePermission: ['IDENTITY_PROVIDERS_DELETE'],
    disablePermission: [],
    sharePermission: [],
  },
  lists: {
    ...defaultEntity,
    sidePanelWidth: 600,
    subEntity: 'listItems',
    selectedSubEntityId: undefined,
    subEntitySaving: false,
    readPermission: ['VIEW_ALL_LISTS'],
    updatePermission: ['MANAGE_ALL_LISTS'],
    createPermission: ['MANAGE_ALL_LISTS'],
  },
  listTypes: {
    ...defaultEntity,
  },
  outboundIdentifiers: {
    ...defaultEntity,
    readPermission: ['OUTBOUND_IDENTIFIER_READ'],
    updatePermission: ['OUTBOUND_IDENTIFIER_MODIFY'],
    createPermission: ['OUTBOUND_IDENTIFIER_CREATE'],
    disablePermission: ['OUTBOUND_IDENTIFIER_DISABLE'],
    assignPermission: ['OUTBOUND_IDENTIFIER_ASSIGN'],
  },
  emailTemplates: {
    ...defaultEntity,
    sidePanelWidth: 650,
    readPermission: ['USER_MANAGEMENT_EMAIL_READ'],
    updatePermission: ['USER_MANAGEMENT_EMAIL_UPDATE', 'PLATFORM_USER_MANAGEMENT_EMAIL_UPDATE'],
  },
  outboundIdentifierLists: {
    ...defaultEntity,
    metaData: {
      listDependency: 'outboundIdentifiers',
    },
    sidePanelWidth: 650,
    readPermission: ['OUTBOUND_IDENTIFIER_READ'],
    updatePermission: ['OUTBOUND_IDENTIFIER_MODIFY'],
    createPermission: ['OUTBOUND_IDENTIFIER_CREATE'],
    disablePermission: ['OUTBOUND_IDENTIFIER_DISABLE'],
    assignPermission: ['OUTBOUND_IDENTIFIER_ASSIGN'],
  },
  chatWidgets: {
    ...defaultEntity,
    readPermission: ['WEB_INTEGRATIONS_APP_READ'],
    updatePermission: ['WEB_INTEGRATIONS_APP_UPDATE'],
    createPermission: ['WEB_INTEGRATIONS_APP_UPDATE'],
  },
  digitalChannelsApps: {
    data: [],
  },
  users: {
    ...defaultEntity,
    sidePanelWidth: 600,
    readPermission: ['VIEW_ALL_USERS', 'PLATFORM_VIEW_ALL_USERS', 'CONFIG_USERS_VIEW', 'PLATFORM_CONFIG_USERS_VIEW'],
    updatePermission: [
      'PLATFORM_MANAGE_ALL_TENANTS_ENROLLMENT',
      'MANAGE_ALL_USER_EXTENSIONS',
      'MANAGE_ALL_GROUP_USERS',
      'MANAGE_ALL_USER_SKILLS',
      'MANAGE_ALL_USER_LOCATIONS',
      'MANAGE_TENANT_ENROLLMENT',
      'MANAGE_SKILL_PROFICIENCY',
    ],
    createPermission: ['PLATFORM_MANAGE_ALL_TENANTS_ENROLLMENT', 'MANAGE_TENANT_ENROLLMENT'],
  },
  platformRoles: {
    ...defaultEntity,
  },
  roles: {
    ...defaultEntity,
    readPermission: ['VIEW_ALL_ROLES'],
    updatePermission: [
      'PLATFORM_MANAGE_ALL_TENANTS_ENROLLMENT',
      'PLATFORM_CREATE_TENANT_ROLES',
      'VIEW_ALL_ROLES',
      'MANAGE_ALL_ROLES',
      'MANAGE_TENANT_ENROLLMENT',
    ],
    createPermission: ['PLATFORM_CREATE_TENANT_ROLES', 'MANAGE_ALL_ROLES'],
    sharePermission: ['PLATFORM_MANAGE_ALL_TENANTS_ENROLLMENT', 'MANAGE_ALL_ROLES'],
  },
  permissions: {
    ...defaultEntity,
    readPermission: ['VIEW_ALL_ROLES'],
    updatePermission: [
      'PLATFORM_MANAGE_ALL_TENANTS_ENROLLMENT',
      'PLATFORM_CREATE_TENANT_ROLES',
      'VIEW_ALL_ROLES',
      'MANAGE_ALL_ROLES',
      'MANAGE_TENANT_ENROLLMENT',
    ],
  },
  skills: {
    ...defaultEntity,
    metaData: {
      listDependency: 'users',
    },
    sidePanelWidth: 650,
    readPermission: ['VIEW_ALL_SKILLS', 'MANAGE_SKILL_PROFICIENCY'],
    updatePermission: [
      'PLATFORM_MANAGE_ALL_TENANTS_ENROLLMENT',
      'MANAGE_ALL_SKILLS',
      'MANAGE_ALL_USER_SKILLS',
      'MANAGE_TENANT_ENROLLMENT',
      'MANAGE_SKILL_PROFICIENCY',
    ],
    createPermission: [
      'PLATFORM_MANAGE_ALL_TENANTS_ENROLLMENT',
      'MANAGE_ALL_SKILLS',
      //    'MANAGE_ALL_USER_SKILLS',
      'MANAGE_TENANT_ENROLLMENT',
    ],
  },
  groups: {
    ...defaultEntity,
    metaData: {
      listDependency: 'users',
    },
    readPermission: ['VIEW_ALL_GROUPS'],
    updatePermission: [
      'PLATFORM_MANAGE_ALL_TENANTS_ENROLLMENT',
      'MANAGE_ALL_GROUPS',
      'MANAGE_ALL_GROUP_USERS',
      'MANAGE_ALL_GROUP_OWNERS',
      'MANAGE_TENANT_ENROLLMENT',
    ],
    createPermission: [
      'PLATFORM_MANAGE_ALL_TENANTS_ENROLLMENT',
      'MANAGE_ALL_GROUPS',
      'MANAGE_ALL_GROUP_USERS',
      'MANAGE_ALL_GROUP_OWNERS',
      'MANAGE_TENANT_ENROLLMENT',
    ],
  },
  dashboards: {
    ...defaultEntity,
  },
  standardDashboards: {
    ...defaultEntity,
  },
  forecastDashboards: {
    ...defaultEntity,
  },
  historicalReportFolders: {
    ...defaultEntity,
  },
  dataAccessReports: {
    ...defaultEntity,
    metaData: {
      listDependency: 'users',
    },
    sidePanelWidth: 600,
    readPermission: ['CUSTOM_STATS_READ'],
    updatePermission: ['CUSTOM_STATS_UPDATE'],
    createPermission: ['CUSTOM_STATS_CREATE'],
  },
  reasons: {
    ...defaultEntity,
    readPermission: ['READ_PRESENCE_REASONS'],
    updatePermission: ['UPDATE_PRESENCE_REASONS'],
    createPermission: ['CREATE_PRESENCE_REASONS'],
    disablePermission: ['UPDATE_PRESENCE_REASONS'],
    sharePermission: ['SHARE_PRESENCE_REASONS'],
  },
  reasonLists: {
    ...defaultEntity,
    readPermission: ['READ_REASON_LIST'],
    updatePermission: ['UPDATE_REASON_LIST'],
    createPermission: ['CREATE_REASON_LIST'],
    disablePermission: ['UPDATE_REASON_LIST'],
    sharePermission: ['UPDATE_REASON_LIST'],
  },
  flows: {
    ...defaultEntity,
    sidePanelWidth: 600,
    readPermission: ['VIEW_ALL_FLOWS'],
    updatePermission: ['MANAGE_ALL_FLOWS'],
    createPermission: ['MANAGE_ALL_FLOWS'],
    disablePermission: ['MANAGE_ALL_FLOWS'],
    assignPermission: ['MANAGE_ALL_FLOWS'],
  },
  queues: {
    ...defaultEntity,
    sidePanelWidth: 650,
    readPermission: ['VIEW_ALL_QUEUES'],
    updatePermission: ['MANAGE_ALL_QUEUES'],
    createPermission: ['MANAGE_ALL_QUEUES'],
    disablePermission: ['MANAGE_ALL_QUEUES'],
    assignPermission: ['MANAGE_ALL_QUEUES'],
  },
  transferLists: {
    ...defaultEntity,
    readPermission: ['VIEW_ALL_TRANSFER_LISTS'],
    updatePermission: ['MANAGE_ALL_TRANSFER_LISTS'],
    createPermission: ['MANAGE_ALL_TRANSFER_LISTS'],
    disablePermission: ['MANAGE_ALL_TRANSFER_LISTS'],
    assignPermission: ['MANAGE_ALL_TRANSFER_LISTS'],
  },
  messageTemplates: {
    ...defaultEntity,
    sidePanelWidth: 650,
    readPermission: ['VIEW_ALL_MESSAGE_TEMPLATES'],
    updatePermission: ['MANAGE_ALL_MESSAGE_TEMPLATES'],
    createPermission: ['MANAGE_ALL_MESSAGE_TEMPLATES'],
    disablePermission: ['MANAGE_ALL_MESSAGE_TEMPLATES'],
    assignPermission: ['MANAGE_ALL_MESSAGE_TEMPLATES'],
  },
  dispatchMappings: {
    ...defaultEntity,
    readPermission: ['VIEW_ALL_CONTACT_POINTS'],
    updatePermission: ['MAP_ALL_CONTACT_POINTS'],
    createPermission: ['MAP_ALL_CONTACT_POINTS'],
    disablePermission: ['MAP_ALL_CONTACT_POINTS'],
  },
  dispositions: {
    ...defaultEntity,
    readPermission: ['READ_DISPOSITIONS'],
    updatePermission: ['UPDATE_DISPOSITIONS'],
    createPermission: ['CREATE_DISPOSITIONS'],
    sharePermission: ['SHARE_DISPOSITIONS'],
  },
  slas: {
    ...defaultEntity,
    sidePanelWidth: 650,
    readPermission: ['CUSTOM_STATS_READ'],
    updatePermission: ['CUSTOM_STATS_UPDATE'],
    createPermission: ['CUSTOM_STATS_CREATE'],
    sharePermission: ['CUSTOM_STATS_UPDATE'],
    disablePermission: [],
    assignPermission: [],
  },
  tenants: {
    ...defaultEntity,
    readPermission: [
      'PLATFORM_VIEW_ALL_TENANTS',
      'PLATFORM_MANAGE_ALL_TENANTS',
      'PLATFORM_CREATE_ALL_TENANTS',
      'PLATFORM_CREATE_TENANT_ROLES',
      'PLATFORM_MANAGE_ALL_TENANTS_ENROLLMENT',
      'MANAGE_TENANT',
    ],
    updatePermission: ['PLATFORM_MANAGE_ALL_TENANTS', 'MANAGE_TENANT'],
    createPermission: ['CREATE_CHILD_TENANT'],
    disablePermission: ['MANAGE_TENANT', 'PLATFORM_MANAGE_ALL_TENANTS'],
    assignPermission: [],
  },
  flowDebugger: {
    ...defaultEntity,
  },
  apiKeys: {
    ...defaultEntity,
    readPermission: ['MANAGE_ALL_APP_CREDENTIALS'],
    updatePermission: ['MANAGE_ALL_APP_CREDENTIALS'],
    createPermission: ['MANAGE_ALL_APP_CREDENTIALS'],
    disablePermission: [],
    assignPermission: [],
  },
  businessHours: {
    ...defaultEntity,
    ...businessHourPermisssions,
  },
  dispositionLists: {
    ...defaultEntity,
    readPermission: ['READ_DISPOSITION_LIST'],
    updatePermission: ['UPDATE_DISPOSITION_LIST'],
    createPermission: ['CREATE_DISPOSITION_LIST'],
    sharePermission: ['UPDATE_DISPOSITION_LIST'],
    disablePermission: [''],
    assignPermission: [''],
  },
  businessHoursV2: {
    subEntity: 'drafts',
    data: [],
    selectedEntityId: '',
    confirmationDialogType: undefined,
    ...businessHourPermisssions,
  },
  customAttributes: {
    ...defaultEntity,
    readPermission: ['INTERACTION_ATTRIBUTES_CONFIG_READ', 'PLATFORM_VIEW_ALL'],
    createPermission: ['INTERACTION_ATTRIBUTES_CONFIG_CREATE', 'PLATFORM_MANAGE_ALL_INTERACTION_ATTRIBUTES'],
    updatePermission: ['INTERACTION_ATTRIBUTES_CONFIG_UPDATE', 'PLATFORM_MANAGE_ALL_INTERACTION_ATTRIBUTES'],
  },
  integrations: {
    ...defaultEntity,
    readPermission: ['VIEW_ALL_PROVIDERS'],
    updatePermission: ['MANAGE_ALL_PROVIDERS'],
    createPermission: ['MANAGE_ALL_PROVIDERS'],
    disablePermission: [],
    assignPermission: [],
  },
  timezones: {
    data: [],
  },
  regions: {
    data: [],
  },
  contactAttributes: {
    ...defaultEntity,
    readPermission: ['CONTACTS_ATTRIBUTES_READ'],
    updatePermission: ['CONTACTS_ATTRIBUTES_UPDATE'],
    createPermission: ['CONTACTS_ATTRIBUTES_CREATE'],
  },
  contactLayouts: {
    ...defaultEntity,
    readPermission: ['CONTACTS_LAYOUTS_READ'],
    updatePermission: ['CONTACTS_LAYOUTS_UPDATE'],
    createPermission: ['CONTACTS_LAYOUTS_CREATE'],
  },
  userProfile: {
    updatePermission: ['PLATFORM_MANAGE_USER_ACCOUNT'],
  },
  capacityRules: {
    ...defaultEntity,
    readPermission: ['MANAGE_ALL_CAPACITY_RULES'],
    updatePermission: ['MANAGE_ALL_CAPACITY_RULES'],
    createPermission: ['MANAGE_ALL_CAPACITY_RULES'],
  },
  whatsappIntegrations: {
    ...defaultEntity,
    readPermission: ['WHATSAPP_INTEGRATIONS_APP_READ'],
    updatePermission: ['WHATSAPP_INTEGRATIONS_APP_UPDATE'],
    createPermission: ['WHATSAPP_INTEGRATIONS_APP_UPDATE'],
    disablePermission: ['WHATSAPP_INTEGRATIONS_APP_UPDATE'],
    assignPermission: [],
  },
  whatsappApps: {
    data: [],
  },
  facebookIntegrations: {
    ...defaultEntity,
    readPermission: ['FACEBOOK_INTEGRATIONS_APP_READ'],
    updatePermission: ['FACEBOOK_INTEGRATIONS_APP_UPDATE'],
    createPermission: ['FACEBOOK_INTEGRATIONS_APP_UPDATE'],
    disablePermission: ['FACEBOOK_INTEGRATIONS_APP_UPDATE'],
    assignPermission: [],
  },
  media: {
    ...defaultEntity,
    readPermission: ['VIEW_ALL_MEDIA', 'PLATFORM_VIEW_ALL'],
    updatePermission: ['MANAGE_ALL_MEDIA', 'PLATFORM_VIEW_ALL'],
    createPermission: ['MANAGE_ALL_MEDIA', 'PLATFORM_VIEW_ALL'],
    disablePermission: ['MANAGE_ALL_MEDIA', 'PLATFORM_VIEW_ALL'],
  },
  wfm: {
    ...defaultEntity,
    readPermission: ['CXWFM_FORECAST_READ'],
    updatePermission: ['CXWFM_FORECAST_WRITE'],
    createPermission: ['CXWFM_FORECAST_WRITE'],
  },
  interactionManagement: {
    ...defaultEntity,
    readPermission: ['TERMINATE_INTERACTIONS'],
    updatePermission: ['TERMINATE_INTERACTIONS'],
  },
  alps: {
    ...defaultEntity,
    readPermission: ['ARTIFACTS_LIFECYCLE_POLICY_READ', 'PLATFORM_ARTIFACTS_LIFECYCLE_POLICY_READ'],
    createPermission: ['ARTIFACTS_LIFECYCLE_POLICY_CREATE', 'PLATFORM_ARTIFACTS_LIFECYCLE_POLICY_MANAGE_ALL'],
    updatePermission: [
      'ARTIFACTS_LIFECYCLE_POLICY_CREATE',
      'ARTIFACTS_LIFECYCLE_POLICY_UPDATE',
      'PLATFORM_ARTIFACTS_LIFECYCLE_POLICY_MANAGE_ALL',
    ],
    disablePermission: ['ARTIFACTS_LIFECYCLE_POLICY_DELETE', 'PLATFORM_ARTIFACTS_LIFECYCLE_POLICY_MANAGE_ALL'],
    termsFormOpen: false,
    pendingSubmit: fromJS({}),
    artifactTypes: [
      {
        artifactTypeName: 'EMAIL',
        artifactType: 'EMAIL',
      },
      {
        artifactTypeName: 'Messaging Transcript',
        artifactType: 'Messaging Transcript',
      },
      {
        artifactTypeName: 'Screen Recording',
        artifactType: 'Screen Recording',
      },
      {
        artifactTypeName: 'Audio Recording',
        artifactType: 'Audio Recording',
      },
    ],
  },
  amr: {
    ...defaultEntity,
    readPermission: ['ARTIFACTS_MANAGEMENT_REQUESTS_READ', 'PLATFORM_ARTIFACTS_MANAGEMENT_REQUESTS_READ'],
    createPermission: [
      'ARTIFACTS_MANAGEMENT_REQUESTS_CREATE_LOCK',
      'ARTIFACTS_MANAGEMENT_REQUESTS_REMOVE_LOCK',
      'ARTIFACTS_MANAGEMENT_REQUESTS_DELETE_ARTIFACT',
    ],
    addLock: ['ARTIFACTS_MANAGEMENT_REQUESTS_CREATE_LOCK'],
    removeLock: ['ARTIFACTS_MANAGEMENT_REQUESTS_REMOVE_LOCK', 'PLATFORM_ARTIFACTS_MANAGEMENT_REQUESTS_REMOVE_LOCK'],
    deleteLock: [
      'ARTIFACTS_MANAGEMENT_REQUESTS_DELETE_ARTIFACT',
      'PLATFORM_ARTIFACTS_MANAGEMENT_REQUESTS_DELETE_ARTIFACT',
    ],
    termsFormOpen: false,
    pendingSubmit: fromJS({}),
    requestType: [
      {
        requestTypeName: 'Add Lock',
        requestType: 'add-lock',
      },
      {
        requestTypeName: 'Remove Lock',
        requestType: 'remove-lock',
      },
      {
        requestTypeName: 'Delete',
        requestType: 'delete',
      },
    ],
  },
  //hygen-inject-before
});

export const userMenus = ['users', 'groups', 'skills', 'roles', 'reasons', 'reasonLists', 'capacityRules'];

export const configurationMenus = [
  'tenants',
  'identityProviders',
  'integrations',
  'slas',
  'emailTemplates',
  'lists',
  'outboundIdentifiers',
  'outboundIdentifierLists',
  'chatWidgets',
  'whatsappIntegrations',
  'facebookIntegrations',
  'transferLists',
  'apiKeys',
  'messageTemplates',
  'businessHours',
  'contactAttributes',
  'contactLayouts',
  'alps',
  'amr',
];

export const flowMenus = [
  'flows',
  'dispositions',
  'dispositionLists',
  'queues',
  'media',
  'flowDebugger',
  'dispatchMappings',
  'customAttributes',
];

export const reportingMenus = [
  'dataAccessReports',
  'interactionManagement',
  'interactionMonitoring',
  'agentStateMonitoring',
];
// Actions

export const setCurrentEntity = (entityName) => ({
  type: 'SET_CURRENT_ENTITY',
  entityName,
});
export const setSelectedEntityId = (entityId) => ({
  type: 'SET_SELECTED_ENTITY_ID',
  entityId,
});

export const copyCurrentEntity = () => ({ type: 'COPY_CURRENT_ENTITY' });

export const setSelectedEntityCreate = () => setSelectedEntityId('create');
export const unsetSelectedEntityId = () => setSelectedEntityId('');
export const setEntityUpdating = (entityName, entityId, updating) => ({
  type: 'SET_ENTITY_UPDATING',
  entityName,
  entityId,
  updating,
});

export const toggleBulkEntityChange = (entityName, entityId, bool) => ({
  type: 'TOGGLE_BULK_ENTITY_CHANGE',
  entityName,
  entityId,
  bool,
});

export const onFormButtonSubmit = () => ({ type: 'START_FORM_SUBMISSION' });
export const onFormSubmit = (values, { dirty }) => ({
  type: 'FORM_SUBMIT',
  values,
  dirty,
});
export const updateCapacityRule = (values, entityName) => ({
  values,
  entityName,
  type: 'UPDATE_USER_CAPACITY_RULE',
});
// Code related to MS Teams Integration for Sync Users API
export const syncUsersEntity = (entity) => ({
  type: 'SYNC_USERS_ENTITY',
  entity,
});
export const syncUsersEntityProgress = (entity) => ({
  type: 'SYNC_USERS_ENTITY_PROGRESS',
  entity,
});
export const toggleMsteamsEntity = (entity) => ({
  type: 'TOGGLE_MSTEAMS_ENTITY',
  entity,
});

export const toggleEntity = (entity) => ({
  type: 'TOGGLE_ENTITY',
  entity,
});

export const toggleEntityListItemActive = (entity) => ({
  type: 'TOGGLE_ENTITY_LIST_ITEM',
  entity,
});
export const addListItem = (listItemId) => ({
  type: 'ADD_LIST_ITEM',
  listItemId,
});
export const toggleListItemEntity = (id, name, actionType) => ({
  type: 'TOGGLE_LIST_ITEM_ENTITY',
  id,
  name,
  actionType,
});
export const openFlowDesigner = (listItemId, row, subEntityName) => ({
  type: 'OPEN_FLOW_DESIGNER',
  listItemId,
  row,
  subEntityName,
});
export const createDraftItem = (listItemId, row, subEntityName) => ({
  type: 'CREATE_DRAFT_ITEM',
  listItemId,
  row,
  subEntityName,
});
export const getVersionFlowData = (listItemId, row, subEntityName) => ({
  type: 'GET_VERSION_FLOW_DATA',
  listItemId,
  row,
  subEntityName,
});
export const copyListItem = (listItemId, row, subEntityName) => ({
  type: 'COPY_LIST_ITEM',
  listItemId,
  row,
  subEntityName,
});
export const removeListItem = (listItemId, subEntityName) => ({
  type: 'REMOVE_LIST_ITEM',
  listItemId,
  subEntityName,
});
export const removeTwilioGlobalDialParam = (subEntityName) => ({
  type: 'REMOVE_TWILIO_GLOBAL_DIAL_PARAM',
  subEntityName,
});
export const updateTwilioGlobalDialParam = (subEntityName) => ({
  type: 'UPDATE_TWILIO_GLOBAL_DIAL_PARAM',
  subEntityName,
});
export const removeEcgGlobalDialParam = (subEntityName) => ({
  type: 'REMOVE_ECG_GLOBAL_DIAL_PARAM',
  subEntityName,
});
export const updateEcgGlobalDialParam = (subEntityName) => ({
  type: 'UPDATE_ECG_GLOBAL_DIAL_PARAM',
  subEntityName,
});
export const setPendingProficiency = (id, newValue) => ({
  type: 'SET_PENDING_PROFICIENCY',
  id,
  newValue,
});
export const updateProficiency = (id) => ({
  type: 'UPDATE_PROFICIENCY',
  id,
});
export const setConfirmationDialog = (modalType, metaData, nextEntity) => ({
  type: 'SET_CONFIRMATION_DIALOG',
  modalType,
  metaData,
  nextEntity,
});
export const toggleProficiency = () => ({
  type: 'TOGGLE_PROFICIENCY',
});

export const executeConfirmCallback = (referenceData) => ({
  type: 'EXECUTE_CONFIRM_CALLBACK',
  referenceData,
});

export const fetchData = (entityName, tableType) => ({
  type: 'FETCH_DATA',
  entityName,
  tableType,
});

export const startSalesforceOauth = (integrationId, loginUrl, tenantId) => ({
  type: 'SF_OAUTH_START',
  integrationId,
  loginUrl,
  tenantId,
});

export const salesforceOauthReturnSuccess = (integrationId) => ({
  type: 'SF_OAUTH_RETURN_SUCCESS',
  integrationId,
});

export const fetchListItems = (entityName, associatedEntityName) => ({
  type: 'FETCH_LIST_ITEMS',
  entityName,
  associatedEntityName,
});

export const fetchReportingEvents = (interactionId) => ({
  type: 'FETCH_REPORTING_EVENTS',
  interactionId,
});

export const setCurrentSubEntity = (subEntityName) => ({
  type: 'SET_CURRENT_SUB_ENTITY',
  subEntityName,
});
export const setSelectedSubEntityId = (selectedSubEntityId) => ({
  type: 'SET_SELECTED_SUB_ENTITY_ID',
  selectedSubEntityId,
});

export const onSubEntityFormSubmit = (values, { dirty }) => ({
  type: 'SUB_ENTITY_FORM_SUBMIT',
  values,
  dirty,
});
export const onCopyListItemFormSubmit = (values, { dirty }) => ({
  type: 'COPY_LIST_ITEM_FORM_SUBMIT',
  values,
  dirty,
});
export const onInitialVersionFormSubmit = (values, { dirty }) => ({
  type: 'INITIAL_VERSION_FORM_SUBMIT',
  values,
  dirty,
});
export const updateEntity = (entityName, entityId, values) => ({
  type: 'UPDATE_ENTITY',
  entityName,
  entityId,
  values,
});

export const updateEntityRejected = (entityName, entityId) => ({
  type: 'UPDATE_ENTITY_REJECTED',
  entityName,
  entityId,
});
export const updateSidePanelWidth = (width) => ({
  type: 'UPDATE_SIDE_PANEL_WIDTH',
  width,
});
export const deleteSubEntity = (subEntityId) => ({
  type: 'DELETE_SUB_ENTITY',
  subEntityId,
});

export const downloadCsv = () => ({ type: 'DOWNLOAD_CSV' });

export const uploadCsv = (target) => ({ type: 'UPLOAD_CSV', target });

export const changeUserInviteStatus = (toStatus, userId) => ({
  type: 'CHANGE_USER_INVITE_STATUS',
  toStatus,
  userId,
});

export const toggleShared = () => ({
  type: 'TOGGLE_SHARED',
});

export const setSelectedBusinessHourVersion = (selectedVersionId) => ({
  type: 'SET_SELECTED_BUSINESS_HOUR_VERSION',
  selectedVersionId,
});

export const setTenantUserAsImpersonated = () => ({
  type: 'SET_TENANT_USER_AS_IMPERSONATED',
});

export const removeTransferListItem = (type, deleteEntityId) => {
  if (type === 'transferListItem') {
    return {
      type: 'REMOVE_TRANSFER_LIST_ITEM',
      transferListItemId: deleteEntityId,
    };
  } else if (type === 'categoryItems') {
    return {
      type: 'REMOVE_TRANSFER_LIST_ITEM',
      categoryId: deleteEntityId,
    };
  }
};

export const toggleMessageTemplateText = (isDisplayContentInHtml) => ({
  type: 'TOGGLE_MESSAGE_TEMPLATE_TEXT_CONTENT',
  isDisplayContentInHtml,
});

export const removeReasonListItem = (type, deleteEntityId) => {
  if (type === 'reasonListItem') {
    return {
      type: 'REMOVE_REASON_LIST_ITEM',
      reasonListItemId: deleteEntityId,
    };
  } else if (type === 'categoryItems') {
    return {
      type: 'REMOVE_REASON_LIST_ITEM',
      categoryId: deleteEntityId,
    };
  }
};

export const removeSecretApiKey = () => ({
  type: 'REMOVE_SECRET_API_KEY',
});

export const deleteApiKey = () => ({
  type: 'DELETE_API_KEY',
});

export const updateConfigUIUrlWithQueryString = (entityId) => ({
  type: 'UPDATE_CONFIG_UI_URL_WITH_QUERY_STRING',
  entityId,
});

export const fetchActiveVersionBusinessHoursFulfilled = (activeVersions) => ({
  type: 'FETCH_ACTIVE_VERSION_BUSINESS_HOUR_FULFILLED',
  activeVersions,
});

export const onIntegrationListenerFormSubmit = (values, { dirty }) => ({
  type: 'INTEGRATION_LISTENER_FORM_SUBMIT',
  values,
  dirty,
  entityName: 'integrations',
});

export const publishBusinessHoursV2Draft = (values) => ({
  type: 'PUBLISH_BUSINESS_HOURS_V2_DRAFT',
  values,
});

export const saveBeforePublishBusinessHoursV2Drfat = (values) => ({
  type: 'SAVE_BEFORE_PUBLISH_BUSINESS_HOURS_V2_DRAFT',
  values,
});

export const createDraftBusinessHoursV2 = (businessHourId, values) => ({
  type: 'CREATE_DRAFT_BUSINESS_HOURS_V2',
  businessHourId,
  values,
});

export const UpdateBrandingImageFileInState = (file, name) => ({
  type: 'UPDATE_BRANDING_FILES_IN_STATE',
  file,
  name,
});

export const resetTenantBranding = (entityId) => ({
  type: 'RESET_TENANT_BRANDING_TO_DEFAULT',
  entityId,
});

export const updateUserProfile = (profileEntity, values) => ({
  type: 'UPDATE_USER_PROFILE',
  profileEntity,
  values,
});

export const setShowAdvancedOrBasicQueueQuery = (entityId, versionId, queryType) => ({
  type: 'SHOW_ADVACNED_OR_BASIC_QUEUE_QUERY',
  entityId,
  versionId,
  queryType,
});

export const createQueueVersionCopy = (version) => ({
  type: 'CREATE_QUEUE_VERSION_COPY',
  version,
});

export const convertQueueQueryObjToEdn = (basicQuery) => ({
  type: 'CONVERT_QUEUE_QUERY_TO_EDN',
  basicQuery,
});

export const setImapHost = (imapHost) => ({ type: 'SET_IMAP_HOST', imapHost });

export const setSmtpHost = (smtpHost) => ({ type: 'SET_SMTP_HOST', smtpHost });

export const setSmtpUser = (smtpUser) => ({ type: 'SET_SMTP_USER', smtpUser });

export const fetchOAuthAccessToken = (body, fromListener) => ({ type: 'FETCH_OAUTH_ACCESS_TOKEN', body, fromListener });

export const setOAuthToken = (refreshToken, fromListener) => ({
  type: 'SET_OAUTH_ACCESS_TOKEN',
  refreshToken,
  fromListener,
});

export const setOAuthCredentialId = (refreshToken, fromListener) => ({
  type: 'SET_OAUTH_CREDENTIAL_ID',
  refreshToken,
  fromListener,
});

// Reducer
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case 'SET_CURRENT_ENTITY': {
      return state.set('currentEntity', action.entityName);
    }
    case 'FETCH_WFM_URL_FULFILLED': {
      return state.setIn(['tenants', 'wfmData'], action.response);
    }
    case 'FETCH_TENANT_WFM_URL_FULFILLED': {
      return state.setIn(['tenants', 'wfmDataTenant'], action.response);
    }
    case 'FETCH_WFM_URL_REJECTED': {
      return state.deleteIn([action.entityName, 'fetching']);
    }
    case 'FETCH_TENANT_WFM_URL_REJECTED': {
      return state.deleteIn([action.entityName, 'fetching']);
    }
    case 'SYNC_USERS_ENTITY': {
      return state
        .set('msteamsSyncUsersError', false)
        .set('creating', true)
        .setIn(['msteamsUserSync'], 'sync');
    }
    case 'TOGGLE_MSTEAMS_ENTITY': {
      return state.set('creating', true);
    }
    case 'SYNC_USERS_ENTITY_PROGRESS': {
      if (action.response !== undefined) {
        return state.set('msteamsSyncUsersProgressData', action.response);
      }
      return state.set('msteamsSyncUsersProgressData', {});
    }
    case 'SYNC_USERS_ENTITY_PROGRESS_FULFILLED':
      return state.set('msteamsSyncUsersProgressData', action.response);
    case 'SYNC_USERS_ENTITY_FULFILLED': {
      return state
        .set('msteamsSyncUsersError', false)
        .set('msteamsSyncUsersData', action.response)
        .setIn(['creating'], false)
        .setIn(['msteamsUserSync'], '');
    }
    case 'SYNC_USERS_ENTITY_PROGRESS_REJECTED':
    case 'SYNC_USERS_ENTITY_rejected':
      return state.set('msteamsSyncUsersError', true).setIn(['creating'], false);
    case 'SYNC_USERS_ENTITY_REJECTED': {
      return state.set('creating', false).setIn(['msteamsUserSync'], '');
    }
    case 'TOGGLE_ENTITY': {
      const currentEntity = state.get('currentEntity');
      if (currentEntity === 'integrations') {
        return state.set('creating', true).setIn(['msteamsUserSync'], 'toggle');
        //.deleteIn(['msteamsSyncUsersData']);
      } else {
        return state.set('creating', false);
      }
    }
    case 'TOGGLE_ENTITY_REJECTED': {
      return state.set('creating', false);
    }
    case 'REMOVE_SECRET_API_KEY': {
      const entityIndex = findEntityIndex(state, 'apiKeys', state.getIn(['apiKeys', 'selectedEntityId']));
      if (entityIndex !== -1) {
        return state.deleteIn(['apiKeys', 'data', entityIndex, 'secret']);
      } else {
        return state;
      }
    }
    case 'DELETE_API_KEY_FULFILLED': {
      const entityIndex = findEntityIndex(state, 'apiKeys', action.sdkCall.data.apiKeyId);
      if (entityIndex !== -1) {
        return state.deleteIn(['apiKeys', 'data', entityIndex]).setIn(['apiKeys', 'selectedEntityId'], '');
      } else {
        return state;
      }
    }
    case 'UPDATE_TWILIO_GLOBAL_DIAL_PARAM':
    case 'UPDATE_ECG_GLOBAL_DIAL_PARAM': {
      const entityIndex = getSelectedEntityWithIndex(state).get('currentIndex');
      if (entityIndex !== -1) {
        return state.setIn([state.get('currentEntity'), 'selectedSubEntityId'], action.subEntityName);
      } else {
        return state;
      }
    }
    case 'SET_CONFIRMATION_DIALOG': {
      const updatedState = state
        .update(state.get('currentEntity'), (entityStore) =>
          entityStore.set('confirmationDialogType', action.modalType).set('confirmationDialogMetaData', action.metaData)
        )
        .delete('nextEntity');
      if (action.nextEntity) {
        return updatedState.set('nextEntity', action.nextEntity);
      } else {
        return updatedState;
      }
    }
    case 'SET_SELECTED_ENTITY_ID': {
      const currentEntity = state.get('currentEntity');

      const updatedState = state
        .setIn([currentEntity, 'selectedEntityId'], action.entityId)
        // We uncheck all rows if form is closed and
        // we were performing bulk actions
        .updateIn(
          [currentEntity, 'data'],
          (entityData) =>
            action.entityId === ''
              ? entityData && entityData.map((item) => item.set('bulkChangeItem', false))
              : entityData
        );
      return updatedState;
    }
    case 'UPDATE_USER_PERMISSIONS': {
      const { tenantId } = action.tenantInfo;
      return state.set('currentTenantId', tenantId);
    }
    case 'SET_AS_ACTIVE_TENANT': {
      const { tenantId, setAsActiveTenant } = action;
      if (setAsActiveTenant) {
        return state
          .set('currentTenantId', tenantId)
          .deleteIn(['msteamsSyncUsersData'])
          .setIn(['tenants', 'selectedEntityId'], tenantId)
          .updateIn(['branding', 'data'], () => undefined)
          .setIn(['branding', 'isUpdating'], true);
      } else {
        return state.set('currentTenantId', tenantId).deleteIn(['msteamsSyncUsersData']);
      }
    }
    case 'FETCH_DATA': {
      if (action.currentEntityName === 'tenants' && !action.isPlatformEntity) {
        const entityIndex = findEntityIndex(state, action.currentEntityName, action.entityId);
        if (entityIndex !== -1) {
          const updatedState = state.setIn(
            [action.currentEntityName, 'data', entityIndex, 'dependentEntities', action.entityName, 'fetching'],
            true
          );
          return action.entityName === 'branding' ? updatedState.deleteIn(['branding', 'isUpdating']) : updatedState;
        }
      } else if (action.entityName === 'contactLayouts') {
        // We need to fetch attributes freshly, each time user navigates to contact-layouts page.
        return state.setIn([action.entityName, 'fetching'], true).setIn(['contactAttributes', 'fetching'], true);
      }
      return state.setIn([action.entityName, 'fetching'], true);
    }
    case 'FETCH_DATA_FULFILLED': {
      // As we receive the data we tag on the items that are considered inherited
      const { currentEntityName, entityName, response: { result } } = action;

      switch (entityName) {
        case 'roles': {
          const newResult = result.map((entity) => ({
            ...entity,
            inherited: entity.type === 'system' || entity.tenantId !== state.get('currentTenantId'),
          }));
          return state.setIn([entityName, 'data'], fromJS(newResult)).deleteIn([action.entityName, 'fetching']);
        }
        case 'apiKeys': {
          const newResult = result.map((entity) => ({
            ...entity,
            active: entity.status === 'enabled',
          }));
          return state.setIn([entityName, 'data'], fromJS(newResult)).deleteIn([action.entityName, 'fetching']);
        }
        case 'reasonLists': {
          const newResult = result.map((entity) => ({
            ...entity,
            inherited: entity.tenantId !== state.get('currentTenantId'),
          }));
          return state.setIn([entityName, 'data'], fromJS(newResult)).deleteIn([action.entityName, 'fetching']);
        }
        case 'groups': {
          const newResult = result.map((entity) => ({
            ...entity,
            inherited: entity.name === 'everyone',
          }));
          return state.setIn([entityName, 'data'], fromJS(newResult)).deleteIn([action.entityName, 'fetching']);
        }
        case 'lists': {
          const newResult = result.map((entity) => ({
            ...entity,
            inherited: entity.tenantId !== state.get('currentTenantId'),
          }));
          return state.setIn([entityName, 'data'], fromJS(newResult)).deleteIn([action.entityName, 'fetching']);
        }
        case 'contactAttributes': {
          const newResult = result.map((entity) => ({
            ...entity,
            inherited: entity.tenantId !== state.get('currentTenantId'),
          }));
          return state.setIn([entityName, 'data'], fromJS(newResult)).deleteIn([action.entityName, 'fetching']);
        }
        case 'users': {
          // tenants page has dependentEntities whcih changes based on the selected tenant
          if (currentEntityName === 'tenants' && action.entityId !== 'create' && action.entityId !== '') {
            const entityIndex = findEntityIndex(state, currentEntityName, action.entityId);
            if (entityIndex !== -1) {
              return state
                .setIn(
                  [currentEntityName, 'data', entityIndex, 'dependentEntities', entityName, 'data'],
                  fromJS(result)
                )
                .deleteIn([currentEntityName, 'data', entityIndex, 'dependentEntities', entityName, 'fetching']);
            } else {
              return state;
            }
          } else {
            const newResult = result.map((entity) => {
              let newEntity = { ...entity };
              newEntity.extensions.forEach((ext) => (ext.id = generateUUID()));
              return newEntity;
            });
            return state.setIn([entityName, 'data'], fromJS(newResult)).deleteIn([action.entityName, 'fetching']);
          }
        }
        case 'businessHours': {
          const newResult = result.map((entity) => ({
            ...entity,
            businessHoursType: Object.entries(entity)
              .filter(([key]) => key.includes('TimeMinutes'))
              .every(([key, value]) => value <= 0)
              ? '24/7'
              : 'scheduledHours',
          }));
          return state.setIn([entityName, 'data'], fromJS(newResult)).deleteIn([entityName, 'fetching']);
        }
        case 'slas':
        case 'branding':
        case 'integrations':
        case 'identityProviders': {
          // tenants page has dependentEntities whcih changes based on the selected tenant
          if (currentEntityName === 'tenants') {
            const entityIndex = findEntityIndex(state, currentEntityName, action.entityId);
            if (entityIndex !== -1) {
              const updatedState = state
                .setIn(
                  [currentEntityName, 'data', entityIndex, 'dependentEntities', entityName, 'data'],
                  fromJS(result)
                )
                .deleteIn([currentEntityName, 'data', entityIndex, 'dependentEntities', entityName, 'fetching']);
              return entityName === 'branding' && state.get('currentTenantId') === action.entityId
                ? updatedState.setIn(['branding', 'data'], fromJS(result)).deleteIn(['branding', 'fetching'])
                : updatedState;
            } else {
              return state;
            }
          }
          return state.setIn([entityName, 'data'], fromJS(result)).deleteIn([action.entityName, 'fetching']);
        }
        case 'alps': {
          const newResult = result.map((alp) => {
            const activeVersion = alp.versions && alp.versions.find(({ id }) => id === alp.activeVersion);
            // these entities are technically a composite of individual policies who share the same type and subtype.
            // for display purposes, the active policy info is displayed in the side panel
            if (activeVersion) {
              alp = { ...activeVersion, ...alp };
            }
            return { ...alp, id: alp.versionId };
          });

          let finalResult = newResult.filter((item) => item.active == true);

          return state.setIn([entityName, 'data'], fromJS(finalResult)).deleteIn([action.entityName, 'fetching']);
        }
        case 'amr': {
          const newResult = result.map((amr) => {
            let obj = {};
            obj.id = amr.requestId;
            obj.old_id = amr.id;
            obj.name = `Request ${amr.requestId}`;
            delete amr.id;
            amr = {
              ...amr,
              ...obj,
            };
            return amr;
          });
          return state.setIn([entityName, 'data'], fromJS(newResult)).deleteIn([action.entityName, 'fetching']);
        }
        default:
          return state.setIn([entityName, 'data'], fromJS(result)).deleteIn([action.entityName, 'fetching']);
      }
    }
    case 'FETCH_DATA_REJECTED': {
      if (action.currentEntityName === 'tenants' && !action.isPlatformEntity) {
        const entityIndex = findEntityIndex(state, action.currentEntityName, action.entityId);
        if (entityIndex !== -1) {
          const updatedState = state
            .setIn(
              [action.currentEntityName, 'data', entityIndex, 'dependentEntities', action.entityName, 'data'],
              new List()
            )
            .deleteIn([
              action.currentEntityName,
              'data',
              entityIndex,
              'dependentEntities',
              action.entityName,
              'fetching',
            ]);
          return action.entityName === 'branding'
            ? updatedState.deleteIn(['branding', 'fetching']).deleteIn(['branding', 'isUpdating'])
            : updatedState;
        }
      }
      return state.setIn([action.entityName, 'data'], new List()).deleteIn([action.entityName, 'fetching']);
    }
    case 'FETCH_DATA_ITEM': {
      return state.setIn([action.entityName, 'fetching'], true);
    }
    case 'SET_SELECTED_ENTITY_ID_FULFILLED':
    case 'FETCH_DATA_ITEM_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.id);
      const currentEntity = state.get('currentEntity');
      if (entityIndex !== -1) {
        const resp = action?.response?.result ? action.response.result : [];
        const updatedState = state.mergeIn(
          [action.entityName, 'data', entityIndex],
          fromJS({ ...resp, updating: false })
        );
        // queues page needs to fetch queue-versions after creating a new queue,
        // that is why we are setting "creating" && "selectedEntity" here:
        if (action.entityName === 'queues' && action.createdNewQueue) {
          return updatedState
            .setIn([action.entityName, 'selectedEntityId'], action.id)
            .setIn([action.entityName, 'creating'], false)
            .deleteIn([action.entityName, 'fetching']);
        } else if (action.entityName === 'alps') {
          return state;
        } else if (action.entityName === 'amr') {
          return updatedState.setIn([action.entityName, 'viewData'], fromJS(action.response.result));
        } else if (
          action.entityName === 'integrations' &&
          (action.response?.result?.type === 'twilio' || action.response?.result?.type === 'ecg')
        ) {
          // For Twilio/Ecg Integration, we need to fetch the webRtcLabel/ecgWebRtcLabel (so fetching is not done yet)
          return updatedState;
        } else if (action.entityName === 'integrations' && action.response?.result?.type === 'email') {
          if (action.response.result.properties && action.response.result.properties.oauthCredential) {
            let oauthCredential = action.response.result.properties.oauthCredential;
            const decoded = jwt.decode(oauthCredential.idToken);

            return updatedState.setIn(
              [action.entityName, 'data', entityIndex, 'preferred_username'],
              decoded.preferred_username
            );
          }
        }
        return updatedState.deleteIn([action.entityName, 'fetching']);
      } else if (
        (currentEntity === 'userProfile' && action.entityName === 'users') ||
        (currentEntity === 'users' && action.entityName === 'tenants')
      ) {
        // Add user when we're fetching user data for the userProfile or fetching tenant data for users page
        return state
          .updateIn([action.entityName, 'data'], (data) => data.push(fromJS({ ...action.response.result })))
          .deleteIn([action.entityName, 'fetching']);
      } else {
        return state;
      }
    }
    case 'FETCH_WEB_RTC_LABEL_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.id);
      if (entityIndex !== -1) {
        return state
          .setIn([action.entityName, 'data', entityIndex, 'webRtcLabel'], action.response.result.value)
          .deleteIn([action.entityName, 'fetching']);
      }
      return state;
    }
    case 'FETCH_ECG_WEB_RTC_LABEL_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.id);
      if (entityIndex !== -1) {
        return state
          .setIn([action.entityName, 'data', entityIndex, 'ecgWebRtcLabel'], action.response.result.value)
          .deleteIn([action.entityName, 'fetching']);
      }
      return state;
    }
    case 'FETCH_GOOGLE_OAUTH_CLIENT_ID_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.id);
      if (entityIndex !== -1) {
        return state
          .setIn([action.entityName, 'data', entityIndex, 'googleOauthClientId'], action.response.result.value)
          .deleteIn([action.entityName, 'fetching']);
      }
      return state;
    }
    case 'FETCH_GOOGLE_OAUTH_CLIENT_SECRET_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.id);
      if (entityIndex !== -1) {
        return state
          .setIn([action.entityName, 'data', entityIndex, 'googleOauthClientSecret'], action.response.result.value)
          .deleteIn([action.entityName, 'fetching']);
      }
      return state;
    }
    case 'FETCH_MS_OAUTH_CLIENT_ID_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.id);
      if (entityIndex !== -1) {
        return state
          .setIn([action.entityName, 'data', entityIndex, 'msOauthClientId'], action.response.result.value)
          .deleteIn([action.entityName, 'fetching']);
      }
      return state;
    }
    case 'FETCH_MS_OAUTH_CLIENT_SECRET_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.id);
      if (entityIndex !== -1) {
        return state
          .setIn([action.entityName, 'data', entityIndex, 'msOauthClientSecret'], action.response.result.value)
          .deleteIn([action.entityName, 'fetching']);
      }
      return state;
    }
    case 'FETCH_OAUTH_ACCESS_TOKEN_RESPONSE_FULFILLED': {
      const selectedEntity = action.selectedEntity.toJS();
      const entityIndex = findEntityIndex(state, action.entityName, selectedEntity.id);
      if (entityIndex !== -1) {
        let oauthCredential = action.response.result.oauthCredential;
        const decoded = jwt.decode(oauthCredential.idToken);
        if (action.fromListener) {
          if (action.subEntityId === 'listeners') {
            return state
              .setIn([action.entityName, 'data', entityIndex, 'oauthAccessTokenResponse'], action.response.result)
              .setIn([action.entityName, 'data', entityIndex, 'newListener'], {
                preferred_username: decoded.preferred_username,
              })
              .deleteIn([action.entityName, 'fetching']);
          } else {
            return state
              .setIn([action.entityName, 'data', entityIndex, 'oauthAccessTokenResponse'], action.response.result)
              .updateIn([action.entityName, 'data', entityIndex, 'listeners'], (subEntityList) => {
                const subEntityIndex = subEntityList.findIndex(
                  (subEntity) => subEntity.get('id') === action.subEntityId
                );
                if (subEntityIndex !== -1) {
                  return subEntityList.setIn([subEntityIndex, 'preferred_username'], decoded.preferred_username);
                }
                return subEntityList;
              })
              .deleteIn([action.entityName, 'fetching']);
          }
        } else {
          return state
            .setIn([action.entityName, 'data', entityIndex, 'oauthAccessTokenResponse'], action.response.result)
            .setIn([action.entityName, 'data', entityIndex, 'preferred_username'], decoded.preferred_username)
            .deleteIn([action.entityName, 'fetching']);
        }
      }
      return state;
    }
    case 'FETCH_PRIORITY_BASE_ROUTING_FULFILLED': {
      let matchmaker2Enabled;
      if (action.response.result) {
        matchmaker2Enabled = action.response.result.filter((flag) => flag.name === 'matchmaker2-enabled');
      }
      return state
        .setIn([action.entityName, 'matchmaker2Enabled'], matchmaker2Enabled ? matchmaker2Enabled[0]?.value : false)
        .deleteIn([action.entityName, 'fetching']);
    }
    case 'FETCH_DATA_FLOW': {
      return state.setIn([action.entityName, 'fetching'], true);
    }
    case 'FETCH_DATA_FLOW_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.id);
      if (entityIndex !== -1) {
        return state
          .mergeIn([action.entityName, 'data', entityIndex], fromJS({ ...action.response.result }))
          .deleteIn([action.entityName, 'fetching']);
      } else {
        return state;
      }
    }
    case 'FETCH_DATA_ITEM_REJECTED': {
      // queues page needs to fetch queue-versions after creating a new queue,
      // that is why we are setting "creating" && "selectedEntity" here:
      if (action.entityName === 'queues' && action.createdNewQueue) {
        return setEntityUpdatingHelper(state, action, false)
          .setIn(['queues', 'selectedEntityId'], action.id)
          .setIn([action.entityName, 'creating'], false);
      }
      return setEntityUpdatingHelper(state, action, false);
    }
    case 'FETCH_WEB_RTC_LABEL_REJECTED':
    case 'FETCH_ECG_WEB_RTC_LABEL_REJECTED':
    case 'FETCH_GOOGLE_OAUTH_CLIENT_ID_REJECTED':
    case 'FETCH_GOOGLE_OAUTH_CLIENT_SECRET_REJECTED':
    case 'FETCH_MS_OAUTH_CLIENT_ID_REJECTED':
    case 'FETCH_MS_OAUTH_CLIENT_SECRET_REJECTED':
    case 'FETCH_OAUTH_ACCESS_TOKEN_RESPONSE_REJECTED':
    case 'FETCH_PRIORITY_BASE_ROUTING_REJECTED': {
      return state.deleteIn([action.entityName, 'fetching']);
    }
    case 'BULK_ENTITY_UPDATE': {
      return state.setIn([action.entityName, 'bulkUpdating'], true);
    }
    case 'BULK_ENTITY_UPDATE_rejected':
    case 'BULK_ENTITY_UPDATE_cancelled': {
      return state.deleteIn([state.get('currentEntity'), 'bulkUpdating']);
    }
    case 'TOGGLE_BULK_ENTITY_CHANGE': {
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      if (entityIndex !== -1 && action.bool !== undefined) {
        return state
          .mergeIn([action.entityName, 'data', entityIndex], fromJS({ bulkChangeItem: action.bool }))
          .setIn([action.entityName, 'selectedEntityId'], 'bulk');
      } else if (entityIndex !== -1) {
        return state
          .mergeIn(
            [action.entityName, 'data', entityIndex],
            fromJS({
              bulkChangeItem: !state.getIn([action.entityName, 'data', entityIndex, 'bulkChangeItem'], false),
            })
          )
          .setIn([action.entityName, 'selectedEntityId'], 'bulk');
      } else {
        return state;
      }
    }
    case 'SET_MAPPING_VALUE_INVALID': {
      return state.setIn(['dispatchMappings', 'mappingValueInvalid'], action.invalid);
    }
    case 'CREATE_ENTITY': {
      return state.setIn([action.entityName, 'creating'], true).setIn(['msteamsUserSync'], 'submit');
    }
    case 'CREATE_ENTITY_AMR': {
      return state.setIn([action.entityName, 'submittedValues'], action.values);
    }
    case 'DELETE_ENTITY_AMR': {
      return (
        state
          ///  .setIn([action.entityName, 'selectedEntityId'], true)
          .deleteIn([action.entityName, 'submittedValues'])
          .deleteIn([action.entityName, 'viewData'])
      );
    }
    case 'COPY_CURRENT_ENTITY_FULFILLED':
    case 'CREATE_ENTITY_FULFILLED': {
      let { result } = action.response;

      if (action.entityName === 'dispatchMappings') {
        const entityIndex = findEntityIndex(state, 'flows', result.flowId);
        result.flow = { id: result.flowId, name: state.getIn(['flows', 'data', entityIndex]).get('name') };
      } else if (action.entityName === 'businessHours') {
        result.businessHoursType = Object.entries(result)
          .filter(([key]) => key.includes('TimeMinutes'))
          .every(([key, value]) => value <= 0)
          ? '24/7'
          : 'scheduledHours';
      } else if (action.entityName === 'apiKeys') {
        result.active = result.status === 'enabled';
      } else if (action.entityName === 'alps') {
        action.response.result[0].id = action.response.result[0].versionId;
        result = action.response.result[0];
        state.setIn([action.entityName, 'createdBy'], '');
      } else if (action.entityName === 'amr') {
        var a = {};
        a.createdById = '';
        a.updatedById = '';
        state.setIn([action.entityName, 'creating'], false);
        return state.setIn([action.entityName, 'createdBy'], '');
      }

      const updatedState = state.update(action.entityName, (entityStore) =>
        entityStore.update('data', (data) => data.push(fromJS(result)))
      );

      if (
        action.entityName === 'tenants' ||
        action.entityName === 'businessHoursV2' ||
        action.entityName === 'queues'
      ) {
        return updatedState;
      } else {
        return updatedState.setIn([action.entityName, 'creating'], false);
      }
    }
    case 'CHANGE_VERSION_FULFILLED': {
      return state
        .setIn([action.entityName, 'creating'], false)
        .setIn([action.entityName, 'selectedEntityId'], '')
        .deleteIn([action.entityName, 'changeVersionId'])
        .setIn([action.entityName, 'pendingSubmit'], fromJS({}));
      //   .update(action.entityName, (entityStore) =>
      //   entityStore.update('data', (data) => data.push(fromJS(result)))
      // );
    }
    case 'CHANGE_VERSION_REJECTED': {
      return state
        .setIn([action.entityName, 'creating'], false)
        .deleteIn([action.entityName, 'changeVersionId'])
        .deleteIn([action.entityName, 'pendingSubmit']);
    }
    case 'CREATE_ENTITY_REJECTED':
    case 'CREATE_QUEUE_VERSION_REJECTED': {
      return state.setIn([action.entityName, 'creating'], false);
    }
    case 'UPDATE_ENTITY': {
      if (
        action.entityName === 'transferLists' ||
        action.entityName === 'reasonLists' ||
        action.entityName === 'dispositionLists' ||
        action.entityName === 'contactLayouts'
      ) {
        return setEntityUpdatingHelper(state, action, true).deleteIn([action.entityName, 'selectedSubEntityId']);
      }
      return setEntityUpdatingHelper(state, action, true)
        .setIn(['creating'], true)
        .setIn(['msteamsUserSync'], 'submit');
    }
    case 'SET_PENDING_PROFICIENCY': {
      const { id, newValue } = action;

      const currentEntity = state.get('currentEntity');
      if (currentEntity !== 'skills' && currentEntity !== 'users') {
        // sanity check
        console.error('Trying to update proficiency for an invalid entity!');
        return state;
      }

      const selectedEntityId = state.getIn([currentEntity, 'selectedEntityId']);
      const entityIndex = state
        .getIn([currentEntity, 'data'])
        .findIndex((entity) => entity.get('id') === selectedEntityId);
      return state.setIn([currentEntity, 'data', entityIndex, 'pendingProficiencies', id], newValue);
    }
    case 'UPDATE_PROFICIENCY': {
      const { id } = action;

      const currentEntity = state.get('currentEntity');

      if (currentEntity !== 'skills' && currentEntity !== 'users') {
        // sanity check
        console.error('Trying to update proficiency for an invalid entity!');
        return state;
      }

      // if this is the Skills page then Users is our subentity and vice versa
      // on the users page. Users gets loaded regardless so that is fetched the
      // same for each, but skills are stored differently when on Users page.
      let skillId, userId;
      if (currentEntity === 'skills') {
        skillId = state.getIn([currentEntity, 'selectedEntityId']);
        userId = id;
        const userIndex = state.getIn(['users', 'data']).findIndex((entity) => entity.get('id') === userId);
        const skillIndex = state.getIn(['users', 'data', userIndex, 'skills']).findIndex((entity) => {
          // in case the entity is a bare ID
          if (typeof entity === 'function') {
            return entity.get('id') === skillId;
          } else {
            return entity === skillId;
          }
        });
        const newValue = state.getIn(['skills', 'data', skillIndex, 'pendingProficiencies', id]);
        if (userIndex !== -1) {
          try {
            return state.setIn(['users', 'data', userIndex, 'skills', skillIndex, 'proficiency'], newValue);
          } catch (e) {
            return state.setIn(
              ['users', 'data', userIndex, 'skillsWithProficiency', skillIndex, 'proficiency'],
              newValue
            );
          }
        } else {
          return state;
        }
      } else {
        skillId = id;
        userId = state.getIn([currentEntity, 'selectedEntityId']);
        const userIndex = state.getIn(['users', 'data']).findIndex((entity) => entity.get('id') === userId);
        const skillIndex = state
          .getIn(['users', 'data', userIndex, 'skillsWithProficiency'])
          .findIndex((entity) => entity.get('skillId') === skillId);
        const newValue = state.getIn(['users', 'data', userIndex, 'pendingProficiencies', id]);
        if (userIndex !== -1) {
          return state.setIn(
            ['users', 'data', userIndex, 'skillsWithProficiency', skillIndex, 'proficiency'],
            newValue
          );
        }
      }
    }
    case 'CLEAR_PENDING_PROFICIENCY': {
      const { id } = action;

      const currentEntity = state.get('currentEntity');
      if (currentEntity !== 'skills' && currentEntity !== 'users') {
        // sanity check
        console.error('Trying to update proficiency for an invalid entity!');
        return state;
      }

      const selectedEntityId = state.getIn([currentEntity, 'selectedEntityId']);
      const entityIndex = state
        .getIn([currentEntity, 'data'])
        .findIndex((entity) => entity.get('id') === selectedEntityId);
      return state.deleteIn([currentEntity, 'data', entityIndex, 'pendingProficiencies', id]);
    }
    case 'ENABLE_ALPS_POLICY_FULFILLED': {
      if (action.response.result && action.response.result.length > 0) {
        let actionVersionId = action.response.result[0].versionId;
        let actionResponseEnable = action.response.result[0].enable;

        let entityIndex = state
          .getIn(['alps', 'data'])
          .findIndex((entity) => entity.get('versionId') === actionVersionId);
        let version = state.getIn(['alps', 'artifactVersions']).map((item) => {
          if (item.versionId === actionVersionId) {
            item.enable = actionResponseEnable;
          }
          return item;
        });
        return state
          .setIn(['alps', 'data', entityIndex, 'enable'], actionResponseEnable)
          .setIn(['alps', 'artifactVersions'], version);
      } else {
        return state;
      }
    }
    case 'DISABLE_ALPS_POLICY_FULFILLED': {
      if (action.response.result && action.response.result.length > 0) {
        let actionVersionId = action.response.result[0].versionId;
        let actionResponseEnable = action.response.result[0].enable;

        let entityIndex = state
          .getIn(['alps', 'data'])
          .findIndex((entity) => entity.get('versionId') === actionVersionId);
        let version = state.getIn(['alps', 'artifactVersions']).map((item) => {
          if (item.versionId === actionVersionId) {
            item.enable = actionResponseEnable;
          }
          return item;
        });
        return state
          .setIn(['alps', 'data', entityIndex, 'enable'], actionResponseEnable)
          .setIn(['alps', 'artifactVersions'], version);
      } else {
        return state;
      }
    }
    case 'TOGGLE_ENTITY_FULFILLED':
    case 'UPDATE_ENTITY_FULFILLED':
    case 'CHANGE_USER_INVITE_STATUS_FULFILLED':
    case 'UPDATE_USER_CAPACITY_RULE_FULFILLED':
    case 'UPDATE_PLATFORM_USER_ENTITY_FULFILLED':
    case 'BULK_ENTITY_UPDATE_FULFILLED': {
      const { result } = action.response;
      const entityIndex = findEntityIndex(state, action.entityName, result.id || action.id);

      if (
        action.type === 'BULK_ENTITY_UPDATE_FULFILLED' &&
        action.entityName === 'users' &&
        (action.values.addSkill || action.values.removeSkill || action.values.addGroup || action.values.removeGroup)
      ) {
        const userEntityIndex = findEntityIndex(state, 'users', action.response.result.userId);
        if (action.values.addSkill) {
          return state
            .updateIn(['users', 'data', userEntityIndex, 'skills'], (skills) => skills.push(fromJS(result)))
            .deleteIn(['users', 'bulkUpdating']);
        } else if (action.values.removeSkill) {
          const skillIndex = state.getIn(['users', 'data', userEntityIndex, 'skills']).findIndex((skill) => {
            if (typeof skill === 'object') {
              return result.skillId === (skill.get('skillId') || skill.get('id'));
            } else {
              return result.skillId === skill;
            }
          });
          return state
            .deleteIn(['users', 'data', userEntityIndex, 'skills', skillIndex])
            .deleteIn(['users', 'bulkUpdating']);
        } else if (action.values.addGroup) {
          return state
            .updateIn(['users', 'data', userEntityIndex, 'groups'], (groups) => groups.push(fromJS(result)))
            .deleteIn(['users', 'bulkUpdating']);
        } else if (action.values.removeGroup) {
          const groupIndex = state.getIn(['users', 'data', userEntityIndex, 'groups']).findIndex((group) => {
            if (typeof group === 'object') {
              return result.groupId === (group.get('groupId') || group.get('id'));
            } else {
              return result.groupId === group;
            }
          });
          return state
            .deleteIn(['users', 'data', userEntityIndex, 'groups', groupIndex])
            .deleteIn(['users', 'bulkUpdating']);
        }
      }

      if (entityIndex !== -1) {
        if (action.type !== 'BULK_ENTITY_UPDATE_FULFILLED' && action.entityName === 'dispatchMappings') {
          // We probably haven't fetched flows data when performing
          // bulk actions, so we prevent accesing flows data.
          const flowIndex = findEntityIndex(state, 'flows', result.flowId);
          result.flow = { id: result.flowId, name: state.getIn(['flows', 'data', flowIndex]).get('name') };
        } else if (action.entityName === 'businessHours') {
          result.businessHoursType = Object.entries(result)
            .filter(([key]) => key.includes('TimeMinutes'))
            .every(([key, value]) => value <= 0)
            ? '24/7'
            : 'scheduledHours';
        } else if (action.entityName === 'apiKeys') {
          result.active = result.status === 'enabled';
        } else if (action.type === 'UPDATE_PLATFORM_USER_ENTITY_FULFILLED' && action.entityName === 'users') {
          result.platformRoleId = result.roleId;
          delete result['roleId'];
        }

        let updatedState = state
          .remove('loading')
          .mergeIn([action.entityName, 'data', entityIndex], fromJS({ ...result }))
          .deleteIn([action.entityName, 'bulkUpdating']);

        if (action.entityName === 'identityProviders') {
          if (result.metadataUrl) {
            updatedState = updatedState
              .deleteIn([action.entityName, 'data', entityIndex, 'metadataFile'])
              .deleteIn([action.entityName, 'data', entityIndex, 'newMetadataFile']);
          } else if (result.metadataFile) {
            updatedState = updatedState.deleteIn([action.entityName, 'data', entityIndex, 'metadataUrl']);
          }
        }

        if (action.entityName === 'tenants') {
          return updatedState;
        } else if (action.entityName === 'integrations') {
          if (result.type === 'twilio' || result.type === 'ecg') {
            return updatedState
              .setIn([action.entityName, 'selectedSubEntityId'], undefined)
              .setIn([action.entityName, 'data', entityIndex, 'updating'], false)
              .setIn(['creating'], false);
          } else if (
            (result.type === 'email' || result.type === 'facebook' || result.type === 'salesforce') &&
            !result.active
          ) {
            return updatedState
              .updateIn([action.entityName, 'data', entityIndex, 'listeners'], (listeners) =>
                listeners.map((listener) => listener.set('active', false))
              )
              .setIn([action.entityName, 'data', entityIndex, 'updating'], false)
              .setIn(['creating'], false);
          } else {
            return updatedState
              .setIn([action.entityName, 'data', entityIndex, 'updating'], false)
              .setIn(['creating'], false);
          }
        } else {
          return updatedState.setIn([action.entityName, 'data', entityIndex, 'updating'], false);
        }
      }
      return state.deleteIn([action.entityName, 'bulkUpdating']);
    }
    case 'UPDATE_WEB_RTC_LABEL_FULFILLED': {
      const { result } = action.response;
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      if (entityIndex !== -1) {
        return state.setIn([action.entityName, 'data', entityIndex, 'webRtcLabel'], result.value);
      }
      return state;
    }
    case 'UPDATE_ECG_WEB_RTC_LABEL_FULFILLED': {
      const { result } = action.response;
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      if (entityIndex !== -1) {
        return state.setIn([action.entityName, 'data', entityIndex, 'ecgWebRtcLabel'], result.value);
      }
      return state;
    }
    case 'TOGGLE_LIST_ITEM_ENTITY': {
      return state.set('loading', action.id);
    }
    case 'TOGGLE_LIST_ITEM_ENTITY_FULFILLED': {
      const { actionType, entityName, entityId, name, id, response } = action;
      const entityIndex = findEntityIndex(state, action.entityName, entityId);
      const currentList = state.getIn([entityName, 'data', entityIndex, name]);
      const modifiedList = actionType === 'associate' ? currentList.push(id) : currentList.filter((x) => x !== id);
      if (entityIndex !== -1 && name === 'skills') {
        return state
          .remove('loading')
          .setIn([entityName, 'data', entityIndex, name], modifiedList)
          .updateIn([entityName, 'data', entityIndex, 'skillsWithProficiency'], (list) => {
            if (actionType === 'associate') {
              return list.push(fromJS(response.result));
            } else {
              const itemIndex = list.findIndex((item) => item.get('skillId') === id);
              return list.delete(itemIndex);
            }
          });
      } else if (entityIndex !== -1 && name === 'users') {
        const userIndex = state.getIn(['users', 'data']).findIndex((entity) => entity.get('id') === id);
        return state
          .remove('loading')
          .setIn([entityName, 'data', entityIndex, name], modifiedList)
          .updateIn(['users', 'data', userIndex, 'skills'], (list) => {
            if (actionType === 'associate') {
              return list.push(fromJS({ id: response.result.skillId, proficiency: response.result.proficiency }));
            } else {
              const itemIndex = list.findIndex((item) => item.get('id') === entityId);
              return list.delete(itemIndex);
            }
          });
      } else if (entityIndex !== -1) {
        return state.remove('loading').setIn([entityName, 'data', entityIndex, name], modifiedList);
      } else {
        return state.remove('loading');
      }
    }
    case 'FETCH_LIST_ITEMS_FULFILLED': {
      let idType, response;
      switch (action.associatedEntityName) {
        case 'groups': {
          idType = 'groupId';
          break;
        }
        case 'skills': {
          idType = 'skillId';
          break;
        }
        default: {
          idType = 'id';
        }
      }
      if (action.associatedEntityName === 'outboundIdentifierLists' && action.entityName === 'users') {
        response = action.response.result.assigned;
      } else {
        response = action.response.result;
      }
      if (action.associatedEntityName === 'outboundIdentifierLists' && action.entityName !== 'users') {
        idType = 'id';
      }
      if (action.associatedEntityName === 'users' && action.entityName === 'groups') {
        idType = 'memberId';
      }
      if (action.associatedEntityName === 'users' && action.entityName === 'skills') {
        idType = 'userId';
      }
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      if (entityIndex !== -1) {
        return state.setIn(
          [action.entityName, 'data', entityIndex, action.associatedEntityName],
          fromJS(response.map((x) => x[idType]))
        );
      } else {
        return state;
      }
    }
    case 'FETCH_REPORTING_EVENTS_FULFILLED': {
      return state.set('reportingEvents', action.response);
    }
    case 'UPDATE_ENTITY_REJECTED':
    case 'UPLOAD_CSV_REJECTED': {
      return setEntityUpdatingHelper(state.remove('loading'), action, false);
    }
    case 'SET_ENTITY_UPDATING': {
      return setEntityUpdatingHelper(state, action, action.updating);
    }
    case 'SET_CURRENT_SUB_ENTITY': {
      return state.setIn([state.get('currentEntity'), 'subEntity'], action.subEntityName);
    }
    case 'SET_SELECTED_SUB_ENTITY_ID': {
      return state.setIn([state.get('currentEntity'), 'selectedSubEntityId'], action.selectedSubEntityId);
    }
    case 'SET_ALPS_SELECTED_SUB_ENTITY_ID': {
      return state
        .setIn([state.get('currentEntity'), 'selectedEntityId'], action.selectedSubEntityId)
        .setIn([state.get('currentEntity'), 'selectedSubEntityId'], action.selectedSubEntityId);
    }
    case 'CREATE_DRAFT_ITEM':
    case 'GET_VERSION_FLOW_DATA':

    case 'COPY_LIST_ITEM': {
      return state
        .setIn([state.get('currentEntity'), 'selectedSubEntityId'], action.listItemId)
        .setIn([state.get('currentEntity'), 'selectedSubEntityName'], action.subEntityName)
        .setIn([state.get('currentEntity'), 'selectedSubEntityData'], action.row);
    }
    case 'GET_VERSION_FLOW_DATA_FULLFILLED': {
      return state.setIn([state.get('currentEntity'), 'selectedSubEntityData'], {
        ...action.row,
        flow: action.response.result.flow,
      });
    }
    case 'VIEW_FLOW_DESIGNER_FULLFILED': {
      return state
        .setIn([state.get('currentEntity'), 'selectedSubEntityId'], undefined)
        .deleteIn([state.get('currentEntity'), 'selectedSubEntityName'])
        .deleteIn([state.get('currentEntity'), 'selectedSubEntityData']);
    }
    case 'CREATE_FLOW_VERSION_DRAFT_FULFILLED': {
      if (action.selectedSubEntityId === 'drafts') {
        const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
        if (entityIndex !== -1) {
          return state
            .update(action.entityName, (entityStore) =>
              entityStore
                .updateIn(['data', entityIndex, 'drafts'], (subEntityList) =>
                  subEntityList.push(fromJS(action.response.result))
                )
                .set('selectedSubEntityId', undefined)
                .set('subEntitySaving', false)
            )
            .setIn([state.get('currentEntity'), 'selectedSubEntityId'], undefined)
            .deleteIn([state.get('currentEntity'), 'selectedSubEntityName'])
            .deleteIn([state.get('currentEntity'), 'selectedSubEntityData']);
        } else {
          return state
            .setIn([state.get('currentEntity'), 'selectedSubEntityId'], undefined)
            .deleteIn([state.get('currentEntity'), 'selectedSubEntityName'])
            .deleteIn([state.get('currentEntity'), 'selectedSubEntityData']);
        }
      }
      return state
        .setIn([state.get('currentEntity'), 'selectedSubEntityId'], undefined)
        .deleteIn([state.get('currentEntity'), 'selectedSubEntityName'])
        .deleteIn([state.get('currentEntity'), 'selectedSubEntityData']);
    }
    case 'CREATE_EXCEPTION_FULFILLED': {
      const entityIndex = findEntityIndex(state, 'businessHours', action.entityId);
      if (entityIndex !== -1) {
        return state
          .update('businessHours', (entityStore) =>
            entityStore
              .updateIn(
                ['data', entityIndex, 'exceptions'],
                (exceptions) =>
                  !exceptions ? fromJS([action.response.result]) : exceptions.push(fromJS(action.response.result))
              )
              .set('selectedSubEntityId', undefined)
              .set('subEntitySaving', false)
          )
          .setIn([state.get('currentEntity'), 'selectedSubEntityId'], undefined);
      }
      return state.setIn([state.get('currentEntity'), 'selectedSubEntityId'], undefined);
    }
    case 'CREATE_EXCEPTION_REJECTED': {
      const entityIndex = findEntityIndex(state, 'businessHours', action.entityId);
      if (entityIndex !== -1) {
        return state.setIn(['businessHours', 'subEntitySaving'], false);
      }
      return state.setIn([state.get('currentEntity'), 'selectedSubEntityId'], undefined);
    }
    case 'INITIAL_VERSION_FORM_SUBMIT_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      if (entityIndex !== -1 && action.entityName === 'slas') {
        // When creating a SLA version, we set it as the new default
        return state
          .remove('loading')
          .setIn([action.entityName, 'selectedSubEntityId'], undefined)
          .setIn([action.entityName, 'subEntitySaving'], false);
      }
      return state;
    }
    case 'INTEGRATION_LISTENER_FORM_SUBMIT_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      if (entityIndex !== -1 && action.entityName === 'integrations') {
        return state.update(action.entityName, (entityStore) =>
          entityStore
            .updateIn(['data', entityIndex, 'listeners'], (subEntityList) => {
              if (action.subEntityId === 'listeners') {
                return subEntityList?.push(fromJS({ ...action.response.result }));
              }
              const subEntityIndex = subEntityList?.findIndex(
                (subEntity) => subEntity.get('id') === action.subEntityId
              );
              if (subEntityIndex !== -1) {
                return subEntityList?.setIn([subEntityIndex], fromJS({ ...action.response.result }));
              }
              return subEntityList;
            })
            .set('selectedSubEntityId', undefined)
            .set('subEntitySaving', false)
        );
      }
      return state;
    }
    case 'INTEGRATION_LISTENER_FORM_SUBMIT_REJECTED': {
      return state.setIn([action.entityName, 'subEntitySaving'], false);
    }
    case 'CREATE_SUB_ENTITY':
    case 'UPDATE_SUB_ENTITY':
    case 'INTEGRATION_LISTENER_FORM_SUBMIT': {
      return state.setIn([action.entityName, 'subEntitySaving'], true);
    }
    case 'CREATE_SUB_ENTITY_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      if (entityIndex === -1) {
        return state;
      }
      const usesExactResultValue = action.entityName === 'queues' || action.entityName === 'alps';
      return state.update(action.entityName, (entityStore) =>
        entityStore
          .updateIn(
            // todo: decouple this check to use metadata dependantEntity?
            ['data', entityIndex, usesExactResultValue ? 'versions' : 'items'],
            (subEntityList = List()) => {
              if (usesExactResultValue) {
                return subEntityList.push(fromJS(action.response.result));
              } else {
                const { itemValue, key } = action.response.result;
                return subEntityList.push(fromJS({ ...itemValue, key }));
              }
            }
          )
          .set('selectedSubEntityId', undefined)
          .set('subEntitySaving', false)
      );
    }
    case 'TOGGLE_ENTITY_LIST_ITEM_FULFILLED': {
      const entity = selectedEntity(state);
      const { result } = action.response;
      return state
        .updateIn([entity.type, 'data', entity.index, 'members'], (membersList) => {
          const memberIndex = membersList.findIndex((member) => member.get('id') === result.id);
          if (entity.index !== -1 && memberIndex !== -1) {
            return membersList.setIn([memberIndex], fromJS(result));
          } else {
            return membersList;
          }
        })
        .updateIn([entity.dependency, 'data', entity.dependencyItemIndex(result.id)], (item) =>
          item.merge(fromJS(result))
        );
    }
    case 'REMOVE_LIST_ITEM': {
      return state.set('loading', action.listItemId);
    }
    case 'REMOVE_LIST_ITEM_REJECTED': {
      return state.remove('loading');
    }
    case 'REMOVE_LIST_ITEM_FULFILLED': {
      const entity = selectedEntity(state);
      if (action.entityName === 'flows') {
        const filteredListMembers = state
          .getIn([entity.type, 'data', entity.index, 'drafts'])
          .filterNot((member) => member.get('id') === action.listItemId);
        return state.remove('loading').setIn([entity.type, 'data', entity.index, 'drafts'], filteredListMembers);
      } else if (action.entityName === 'businessHours') {
        const filteredListMembers = state
          .getIn([entity.type, 'data', entity.index, 'exceptions'])
          .filterNot((member) => member.get('id') === action.listItemId);
        return state.remove('loading').setIn([entity.type, 'data', entity.index, 'exceptions'], filteredListMembers);
      } else if (action.entityName === 'businessHoursV2') {
        const filteredListMembers = state
          .getIn([entity.type, 'data', entity.index, 'items'])
          .filterNot((member) => member.get('id') === action.listItemId);
        return state.remove('loading').setIn([entity.type, 'data', entity.index, 'items'], filteredListMembers);
      }
      const filteredListMembers = state
        .getIn([entity.type, 'data', entity.index, 'members'])
        .filterNot((member) => member.get('id') === action.listItemId);
      return state.remove('loading').setIn([entity.type, 'data', entity.index, 'members'], filteredListMembers);
    }
    case 'ADD_LIST_ITEM': {
      return state.set('loading', action.listItemId);
    }
    case 'ADD_LIST_ITEM_REJECTED': {
      return state.remove('loading');
    }
    case 'ADD_LIST_ITEM_FULFILLED': {
      const entity = selectedEntity(state);
      const entityToAdd = state
        .getIn([entity.dependency, 'data'])
        .find((entity) => entity.get('id') === action.listItemId);
      return state
        .remove('loading')
        .updateIn([entity.type, 'data', entity.index, 'members'], (members) => members.push(entityToAdd));
    }
    case 'UPDATE_SUB_ENTITY_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      if (entityIndex !== -1) {
        return state.update(action.entityName, (entityStore) =>
          entityStore
            .updateIn(['data', entityIndex, 'items'], (subEntityList = List()) => {
              const subEntityIndex = subEntityList.findIndex(
                (subEntity) => subEntity.get('key') === action.subEntityId || subEntity.get('id') === action.subEntityId
              );
              if (entityIndex !== -1) {
                const { itemValue, key } = action.response.result;
                return subEntityList.setIn(
                  [subEntityIndex],
                  fromJS({
                    ...itemValue,
                    key,
                  })
                );
              } else {
                return subEntityList;
              }
            })
            .update(
              'selectedSubEntityId',
              (selectedSubEntityId) => (action.entityName === 'businessHoursV2' ? selectedSubEntityId : undefined)
            )
            .set('subEntitySaving', false)
        );
      } else {
        return state;
      }
    }
    case 'CREATE_SUB_ENTITY_REJECTED':
    case 'UPDATE_SUB_ENTITY_REJECTED': {
      return state.setIn([action.entityName, 'subEntitySaving'], false);
    }
    case 'UPDATE_SIDE_PANEL_WIDTH': {
      return state.setIn([state.get('currentEntity'), 'sidePanelWidth'], action.width);
    }
    case 'DELETE_SUB_ENTITY': {
      return setSubEntityDeleting(state.set('loading', action.subEntityId), action, true);
    }
    case 'DELETE_SUB_ENTITY_FULFILLED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      if (entityIndex !== -1) {
        return state
          .update(action.entityName, (entityStore) =>
            entityStore
              .updateIn(['data', entityIndex, 'items'], (subEntityList) => {
                const subEntityIndex = subEntityList.findIndex(
                  (subEntity) => subEntity.get('key') === action.subEntityId
                );
                if (entityIndex !== -1) {
                  return subEntityList.delete(subEntityIndex);
                } else {
                  return subEntityList;
                }
              })
              .set('selectedSubEntityId', undefined)
              .set('subEntitySaving', false)
          )
          .remove('loading');
      } else {
        return state;
      }
    }
    case 'DELETE_SUB_ENTITY_REJECTED': {
      return state.remove('loading');
    }
    case 'FETCH_TENANT_DEFAULT_SLA': {
      const { result } = action.response;

      return state.update(action.entityName, (entityStore) =>
        entityStore.update('data', (data) => data.push(fromJS(result)))
      );
    }
    case '@@redux-form/INITIALIZE':
    case '@@redux-form/CHANGE': {
      if (
        (action.entityName === 'transferLists' ||
          action.entityName === 'reasonLists' ||
          action.entityName === 'dispositionLists' ||
          action.entityName === 'contactLayouts') &&
        state.getIn([action.entityName, 'selectedSubEntityId']) !== undefined
      ) {
        return state.deleteIn([action.entityName, 'selectedSubEntityId']);
      }
      if (action.type === '@@redux-form/INITIALIZE' && action.entityName === 'tenants') {
        const currentEntity = state.get('currentEntity');
        const selectedEntityId = state.getIn([currentEntity, 'selectedEntityId']);
        const a = { entityId: selectedEntityId, entityName: currentEntity };

        if (action.toastMessage) {
          if (action.toastMessage.includes('create')) {
            return state.setIn([action.entityName, 'creating'], false);
          } else {
            return setEntityUpdatingHelper(state, a, false);
          }
        }
      }
      if (
        action.type === '@@redux-form/INITIALIZE' &&
        action.meta &&
        action.meta.form === 'integrationListener:create'
      ) {
        const payload = action.payload.toJS();
        const entity = selectedEntity(state);
        if (payload.properties && payload.properties.oauthCredential) {
          const oauthCredential = payload.properties.oauthCredential;
          const decoded = jwt.decode(oauthCredential.idToken);
          return state.updateIn([entity.type, 'data', entity.index, 'listeners'], (subEntityList) => {
            const subEntityIndex = subEntityList.findIndex((subEntity) => subEntity.get('id') === payload.id);
            if (subEntityIndex !== -1) {
              return subEntityList.setIn([subEntityIndex, 'preferred_username'], decoded.preferred_username);
            }
            return subEntityList;
          });
        }
      }
      return state;
    }
    case '@@redux-form/CHANGE_FULFILLED':
    case '@@redux-form/CHANGE_REJECTED':
    case '@@redux-form/DESTROY': {
      const updatedState = setUserExistsInPlatform(state, action);

      if (action.type === '@@redux-form/DESTROY') {
        const isCustomEntity = (entityName) => action.meta.form.toString().startsWith(entityName);
        if (isCustomEntity('messageTemplates')) {
          return updatedState.deleteIn(['messageTemplates', 'isDisplayContentInHtml']);
        }
        if (isCustomEntity('integrations')) {
          let entities = updatedState.toJS();
          if (entities.integrations && entities.integrations.data) {
            const entityIndex = entities.integrations.data.findIndex((integration) => integration.type === 'email');
            if (entityIndex !== -1) {
              return updatedState
                .deleteIn(['integrations', 'data', entityIndex, 'oauthAccessTokenResponse'])
                .deleteIn(['integrations', 'data', entityIndex, 'preferred_username']);
            }
          }
        }
        if (isCustomEntity('integrationListener:create')) {
          let entities = updatedState.toJS();
          if (entities.integrations && entities.integrations.data) {
            const entityIndex = entities.integrations.data.findIndex((integration) => integration.type === 'email');
            if (entityIndex !== -1) {
              return updatedState.deleteIn(['integrations', 'data', entityIndex, 'newListener']);
            }
          }
        }
      }
      return updatedState;
    }
    case 'TOGGLE_MESSAGE_TEMPLATE_TEXT_CONTENT': {
      return state.setIn(['messageTemplates', 'isDisplayContentInHtml'], action.isDisplayContentInHtml);
    }
    case 'FETCH_ACTIVE_VERSION_BUSINESS_HOUR_FULFILLED': {
      return state.updateIn(['businessHoursV2', 'data'], (businessHours) =>
        businessHours.map((businessHour) => {
          return businessHour.update('versions', (versions = List()) => {
            if (
              action.activeVersions[businessHour.get('id')] &&
              !versions.some((version) => version.get('id') === action.activeVersions[businessHour.get('id')].id)
            ) {
              return versions.push(fromJS(action.activeVersions[businessHour.get('id')]));
            } else {
              return versions;
            }
          });
        })
      );
    }
    case 'SET_BUSINESS_HOUR_VERSIONS_AND_DRAFTS': {
      const currentEntity = state.get('currentEntity');
      const selectedEntityId = state.getIn([currentEntity, 'selectedEntityId']);
      const entityIndex = findEntityIndex(state, currentEntity, selectedEntityId);
      if (entityIndex !== -1) {
        return state
          .setIn([currentEntity, 'data', entityIndex, 'versions'], fromJS(action.versions))
          .setIn([currentEntity, 'data', entityIndex, 'items'], fromJS(action.drafts));
      } else {
        return state;
      }
    }
    case 'PUBLISH_BUSINESS_HOURS_V2_DRAFT_FULFILLED': {
      const index = state
        .getIn(['businessHoursV2', 'data'])
        .findIndex((businessHour) => businessHour.get('id') === action.entityId);
      if (index !== -1) {
        return state
          .updateIn(['businessHoursV2', 'data', index, 'versions'], (versions = List()) =>
            versions.push(
              fromJS({
                ...action.response.result,
              })
            )
          )
          .setIn(['businessHoursV2', 'isPublishingDraft'], false)
          .deleteIn(['businessHoursV2', 'selectedSubEntityId']);
      } else {
        return state;
      }
    }
    case 'PUBLISH_BUSINESS_HOURS_V2_DRAFT_REJECTED':
      return state.setIn(['businessHoursV2', 'isPublishingDraft'], false);
    case 'SET_SELECTED_BUSINESS_HOUR_VERSION': {
      return state.setIn(['businessHoursV2', 'selectedVersion'], action.selectedVersionId);
    }
    case 'PUBLISH_BUSINESS_HOURS_V2_DRAFT':
    case 'SAVE_BEFORE_PUBLISH_BUSINESS_HOURS_V2_DRAFT': {
      return state.setIn(['businessHoursV2', 'isPublishingDraft'], true);
    }
    case 'CREATE_DRAFT_BUSINESS_HOURS_V2': {
      return state.setIn(['businessHoursV2', 'isCreatingDraft'], true);
    }
    case 'CREATE_DRAFT_BUSINESS_HOURS_V2_FULFILLED': {
      const index = state
        .getIn(['businessHoursV2', 'data'])
        .findIndex((businessHour) => businessHour.get('id') === action.businessHourId);
      if (index !== -1) {
        return state.update('businessHoursV2', (businessHoursV2State) =>
          businessHoursV2State
            .updateIn(['data', index, 'items'], (drafts = List()) => drafts.push(fromJS(action.response.result)))
            .set('isCreatingDraft', false)
            .set('creating', false)
            .set('selectedEntityId', action.businessHourId)
            .set('selectedSubEntityId', action.response.result.id)
        );
      } else {
        return state;
      }
    }
    case 'CREATE_DRAFT_BUSINESS_HOURS_V2_REJECTED': {
      return state.setIn(['businessHoursV2', 'isCreatingDraft'], false);
    }
    case 'UPLOAD_LOGO_BRANDING_IMAGE':
    case 'UPLOAD_FAVICON_BRANDING_IMAGE': {
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      const imageType = action.type === 'UPLOAD_LOGO_BRANDING_IMAGE' ? 'logo' : 'favicon';
      if (entityIndex !== -1) {
        return state.setIn(
          [action.entityName, 'data', entityIndex, 'dependentEntities', 'branding', `${imageType}Uploading`],
          true
        );
      } else {
        return state;
      }
    }
    case 'UPLOAD_LOGO_BRANDING_IMAGE_REJECTED':
    case 'UPLOAD_FAVICON_BRANDING_IMAGE_REJECTED': {
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      const imageType = action.type === 'UPLOAD_LOGO_BRANDING_IMAGE_REJECTED' ? 'logo' : 'favicon';
      if (entityIndex !== -1) {
        return state.deleteIn([
          'tenants',
          'data',
          entityIndex,
          'dependentEntities',
          'branding',
          `${imageType}Uploading`,
        ]);
      } else {
        return state;
      }
    }
    case 'UPLOAD_LOGO_BRANDING_IMAGE_FULFILLED':
    case 'UPLOAD_FAVICON_BRANDING_IMAGE_FULFILLED': {
      const { result } = action.response;
      const entityIndex = findEntityIndex(state, action.entityName, action.entityId);
      const brandingData = state.getIn(['tenants', 'data', entityIndex, 'dependentEntities', 'branding', 'data']);
      const imageType = action.type === 'UPLOAD_LOGO_BRANDING_IMAGE_FULFILLED' ? 'logo' : 'favicon';
      if (entityIndex !== -1) {
        if (!brandingData || brandingData.size === 0) {
          const brandingObj = {};
          brandingObj[imageType] = result.url;
          return state
            .setIn(['tenants', 'data', entityIndex, 'dependentEntities', 'branding', 'data'], fromJS(brandingObj))
            .deleteIn(['tenants', 'data', entityIndex, 'dependentEntities', 'branding', `${imageType}Uploading`]);
        } else {
          return state
            .setIn(['tenants', 'data', entityIndex, 'dependentEntities', 'branding', 'data', imageType], result.url)
            .deleteIn(['tenants', 'data', entityIndex, 'dependentEntities', 'branding', `${imageType}Uploading`]);
        }
      } else {
        return state;
      }
    }
    case 'RESET_TENANT_BRANDING_TO_DEFAULT': {
      const a = { entityId: action.entityId, entityName: state.get('currentEntity') };
      return setEntityUpdatingHelper(state, a, true);
    }
    case 'RESET_TENANT_BRANDING_TO_DEFAULT_REJECTED': {
      const a = { entityId: action.entityId, entityName: state.get('currentEntity') };
      return setEntityUpdatingHelper(state, a, false);
    }
    case 'CREATE_TENANT_BRANDING_FULFILLED':
    case 'UPDATE_TENANT_BRANDING_FULFILLED':
    case 'RESET_TENANT_BRANDING_TO_DEFAULT_FULFILLED': {
      const { result } = action.response;
      const entityIndex = findEntityIndex(state, 'tenants', action.entityId);
      if (entityIndex !== -1) {
        return state.setIn(['tenants', 'data', entityIndex, 'dependentEntities', 'branding', 'data'], fromJS(result));
      } else {
        return state;
      }
    }
    case 'BRANDING_HAS_BEEN_UPDATED_SUCESSFULLY!':
    case 'BRANDING_HAS_BEEN_RESET_TO_DEFAULT_SUCESSFULLY!': {
      const { result } = action.response;
      if (result.tenantId === state.get('currentTenantId')) {
        return state.updateIn(['branding', 'data'], () => undefined).setIn(['branding', 'isUpdating'], true);
      } else {
        return state;
      }
    }
    case 'USER_PROFILE_TENANTS_FULFILLED': {
      const { details } = action.response;
      if (details) {
        return state.setIn(['userProfile', 'userTenants'], fromJS(details));
      } else {
        return state;
      }
    }
    case 'UPDATE_USER_PROFILE': {
      return state.setIn(['userProfile', 'updating'], true);
    }
    case 'UPDATE_USER_PROFILE_REJECTED': {
      return state.deleteIn(['userProfile', 'updating']);
    }
    case 'UPDATE_USER_PROFILE_FULFILLED': {
      const { userId, response: { result: { roleId, ...result } }, profileEntity } = action;
      const entityIndex = findEntityIndex(state, 'users', userId);
      return entityIndex !== -1
        ? state
            .mergeIn(
              ['users', 'data', entityIndex],
              fromJS({
                ...result,
                ...(profileEntity === 'platformUser' ? { platformRoleId: roleId } : { roleId }),
              })
            )
            .deleteIn(['userProfile', 'updating'])
        : state.deleteIn(['userProfile', 'updating']);
    }
    case 'FETCH_CAPACITY_RULE_VERSIONS_FULFILLED': {
      const { entityId, response: { result } } = action;
      const entityIndex = findEntityIndex(state, 'capacityRules', entityId);
      return entityIndex !== -1
        ? state.setIn(
            ['capacityRules', 'data', entityIndex, 'items'],
            fromJS(result.map(({ version, ...item }) => ({ ...item, id: version })))
          )
        : state;
    }
    case 'CREATE_QUEUE_VERSION_COPY': {
      const { version } = action;
      return state.setIn(['queues', 'selectedSubEntityId'], version.version);
    }
    case 'SHOW_ADVACNED_OR_BASIC_QUEUE_QUERY': {
      const { entityId, versionId, queryType } = action;
      const entityIndex = findEntityIndex(state, 'queues', entityId);
      return state.updateIn(
        ['queues', 'data', entityIndex, 'versions'],
        (versions) =>
          versions && versions.map((v) => (v.get('version') === versionId ? v.set('showQueryType', queryType) : v))
      );
    }
    case 'CHECK_ACTIVE_ACTION': {
      return state.setIn(['alps', 'checkActiveAction'], action.newValue);
    }
    case 'SET_TERMS_FORM_OPEN': {
      const { termsFormOpen, submitSource, versionId } = action;
      return (
        state
          .setIn(['alps', 'termsFormOpen'], termsFormOpen)
          //.setIn(['alps', 'submitSource'], submitSource )
          .setIn(['alps', 'pendingSubmit', 'submitSource'], submitSource)
          .setIn(['alps', 'changeVersionId', 'versionId'], versionId)
      );
    }
    case 'ADD_TO_PENDING_SUBMIT': {
      const { pendingSubmitValues } = action;
      if (state.get('alps').get('pendingSubmit').size === 0) {
        return state.setIn(['alps', 'pendingSubmit'], pendingSubmitValues);
      } else {
        return state.setIn(
          ['alps', 'pendingSubmit'],
          fromJS({
            ...state
              .get('alps')
              .get('pendingSubmit')
              .toJS(),
            ...pendingSubmitValues.toJS(),
          })
        );
      }
    }
    case 'CLEAR_PENDING_SUBMIT': {
      return state.setIn(['alps', 'pendingSubmit'], fromJS({}));
    }
    case 'FETCH_ARTIFACT_TYPES_FULFILLED': {
      const { artifactTypes, entityType } = action;
      // some artifact types have subtypes
      return state
        .setIn(
          [entityType, 'artifactTypes'],
          artifactTypes.map((artifactType) => {
            artifactType.hasSubtypes = entitiesMetaData.alps.typesWithSubtypes.includes(artifactType.artifactTypeName);
            return artifactType;
          })
        )
        .deleteIn(['alps', 'artifactHistory']);
    }

    case 'FETCH_ARTIFACT_LOAD_HISTORY_FULFILLED': {
      let userDataID = state.getIn(['users', 'data']).map((item) => item.get('id'));

      let data = action.artifactHistory.map((item) => {
        if (userDataID.includes(item.updatedBy)) {
          let idx = userDataID.findIndex((item1) => item1 === item.updatedBy);
          let data = state.getIn(['users', 'data']).get(idx);
          item.updatedBy = data.get('firstName') + ' ' + data.get('lastName');
          return item;
        }
        return item;
      });

      let artifactHistory = data.sort(function(a, b) {
        var c = new Date(a.updatedDate);
        var d = new Date(b.updatedDate);
        return d - c;
      });
      let selectedVerisonId = state.getIn(['alps', 'artifactVersions']).map((item) => item.versionId);

      let artifactHistory1 = artifactHistory.filter((item) => {
        if (selectedVerisonId.includes(item.versionId)) {
          return item;
        }
      });
      return state.setIn(['alps', 'artifactHistory'], artifactHistory1);
    }

    case 'FETCH_ARTIFACT_VERSIONS_FULFILLED': {
      let id = state.getIn(['alps', 'selectedEntityId']);
      const selectedEntity = state.getIn(['alps', 'data']).find((item) => item.get('versionId') == id);

      let versionList = [];
      let finalVersionData = action.artifactVersions.filter((alp) => {
        return selectedEntity.get('artifactSubType') === alp.artifactSubType;
      });

      finalVersionData.sort((a, b) => {
        return a.version - b.version;
      });

      versionList = finalVersionData?.map((alps) => {
        return {
          ...alps,
          versionNum: 'v' + alps.version,
          version: alps.versionId,
        };
      });
      if (versionList) {
        let index = versionList && versionList.findIndex((item) => item.active == true);
        let removedArr = versionList.splice(index, 1);
        let newArtifactVersions = [...removedArr, ...versionList];
        return state.setIn(['alps', 'artifactVersions'], newArtifactVersions).deleteIn(['alps', 'artifactHistory']);
      } else {
        return state.setIn(['alps', 'artifactVersions'], []).deleteIn(['alps', 'artifactHistory']);
      }
    }
    case 'FETCH_AMR_REQUEST_TYPES_FULFILLED': {
      const { requestType } = action;
      // some artifact types have subtypes
      return state.setIn(['amr', 'requestType'], requestType.map((requestType) => requestType));
    }
    case 'FETCH_DATA_AMR_FULFILLED': {
      const { currentEntityName, entityName, response: { result } } = action;

      const newResult = result.map((amr) => {
        let obj = {};
        obj.id = amr.requestId;
        obj.old_id = amr.id;
        obj.name = `Request ${amr.requestId}`;
        delete amr.id;
        amr = {
          ...amr,
          ...obj,
        };
        return amr;
      });
      return state
        .setIn(['amr', 'data'], fromJS(newResult))
        .deleteIn(['amr', 'fetching'])
        .setIn(['amr', 'selectedEntityId'], action.requestId);
    }
    case 'FETCH_AMR_REQUEST_FULFILLED': {
      if (state.getIn(['amr', 'viewData']) == undefined) {
        if (action.interactionIdCount - 1 == 1) {
          window.parent.postMessage('stop', '*');
        }
        return state.setIn(['amr', 'creating'], false).setIn(['amr', 'viewData'], fromJS(action.viewData));
      } else {
        if (state.getIn(['amr', 'viewData']).size == action.interactionIdCount - 1) {
          window.parent.postMessage('stop', '*');
        }
        return state
          .setIn(['amr', 'creating'], false)
          .setIn(['amr', 'viewData'], fromJS([...state.getIn(['amr', 'viewData']).toJSON(), ...action.viewData]));
      }
    }
    default:
      return state;
  }
}

// Reducer helper functions

export const setUserExistsInPlatform = (state, action) => {
  let userExistInPlatform = false;
  let userPlatformRoleId = null;
  if (
    action.meta.form === 'users:create' &&
    action.meta.field === 'email' &&
    action.type === '@@redux-form/CHANGE_FULFILLED'
  ) {
    userExistInPlatform = true;
    if (action.response && action.response.result[0]) {
      userPlatformRoleId = action.response.result[0].roleId;
    }
  }
  return state
    .setIn([state.get('currentEntity'), 'userExistInPlatform'], userExistInPlatform)
    .setIn([state.get('currentEntity'), 'userPlatformRoleId'], userPlatformRoleId);
};

export const setEntityUpdatingHelper = (state, { entityName, entityId }, updating) => {
  const entityIndex = findEntityIndex(state, entityName, entityId);
  if (entityIndex !== -1) {
    return state.setIn([entityName, 'data', entityIndex, 'updating'], updating).setIn(['creating'], false);
  } else {
    return state;
  }
};

const setSubEntityDeleting = (state, { subEntityId }, deleting) => {
  const currentEntity = state.get('currentEntity');
  const selectedEntityId = state.getIn([currentEntity, 'selectedEntityId']);
  const entityIndex = findEntityIndex(state, currentEntity, selectedEntityId);
  const subEntityIndex = state
    .getIn([currentEntity, 'data', entityIndex, 'items'])
    .findIndex((subEntity) => subEntity.get('key') === subEntityId);
  return state.updateIn([currentEntity, 'data', entityIndex, 'items', subEntityIndex], (subEntity) =>
    subEntity.set('deleting', deleting)
  );
};

export function selectedEntity(state) {
  const currentEntity = state.get('currentEntity');
  const dependentEntity = state.getIn([currentEntity, 'metaData', 'listDependency'], undefined);
  const selectedEntityId = state.getIn([currentEntity, 'selectedEntityId']);
  let context = {
    index: state.getIn([currentEntity, 'data']).findIndex((entity) => entity.get('id') === selectedEntityId),
    type: currentEntity,
    dependency: dependentEntity,
    dependencyItemIndex: (itemId) =>
      state.getIn([dependentEntity, 'data']).findIndex((entity) => entity.get('id') === itemId),
  };
  return context;
}
